/**
 * Nexora PDV - IndexedDB offline storage
 *
 * Wraps IndexedDB using the `idb` library with a schema optimized for
 * offline POS operation: product catalog cache, pending sales queue,
 * and sync metadata.
 *
 * @module offline-db
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import { uuidv4 } from '../utils/uuid';

const DB_NAME = 'erpone-pdv-offline';
const DB_VERSION = 1;

const PRODUCT_STORE = 'products';
const PENDING_SALES_STORE = 'pendingSales';
const SYNC_META_STORE = 'syncMeta';

const MAX_PRODUCTS = 10_000;

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

interface ProductRecord {
  /** Primary key: product UUID */
  id: string;
  /** GTIN / EAN-13 barcode */
  code: string;
  /** Product display name */
  name: string;
  /** Sale unit price in BRL cents (integer) */
  salePrice: number;
  /** Stock keeping unit (un, kg, pc, etc.) */
  unit: string;
  /** Current stock balance */
  stock: number;
  /** Timestamp of when this record was cached */
  cachedAt: number;
  /** Tenant the product belongs to */
  tenantId: string;
}

interface PendingSaleRecord {
  /** Auto-generated key */
  id?: number;
  /** Causal operation ID for idempotency (UUID v4) */
  offlineOperationId: string;
  /** Tenant that owns this sale */
  tenantId: string;
  /** The full sale payload to POST */
  payload: Record<string, unknown>;
  /** ISO-8601 timestamp of when the sale was queued */
  queuedAt: string;
  /** Number of retry attempts */
  retryCount: number;
  /** Last error message if sync failed */
  lastError?: string;
}

interface SyncMetaRecord {
  /** Key-like identifier, e.g. "lastSync" */
  key: string;
  /** ISO-8601 timestamp */
  value: string;
}

interface PdvDBSchema extends DBSchema {
  [PRODUCT_STORE]: {
    key: string;
    value: ProductRecord;
    indexes: {
      'by-code': string;
      'by-name': string;
      'by-tenant': string;
    };
  };
  [PENDING_SALES_STORE]: {
    key: number;
    value: PendingSaleRecord;
    indexes: {
      'by-queued': number;
    };
  };
  [SYNC_META_STORE]: {
    key: string;
    value: SyncMetaRecord;
  };
}

// ---------------------------------------------------------------------------
// Database singleton
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBPDatabase<PdvDBSchema>> | null = null;

/**
 * Lazy-initialized database connection.
 * Creates object stores and indexes on first open.
 */
function getDb(): Promise<IDBPDatabase<PdvDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<PdvDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // -- Products store --
        if (!db.objectStoreNames.contains(PRODUCT_STORE)) {
          const productStore = db.createObjectStore(PRODUCT_STORE, {
            keyPath: 'id',
          });
          productStore.createIndex('by-code', 'code', { unique: true });
          productStore.createIndex('by-name', 'name', { unique: false });
          productStore.createIndex('by-tenant', 'tenantId', { unique: false });
        }

        // -- Pending sales store --
        if (!db.objectStoreNames.contains(PENDING_SALES_STORE)) {
          const pendingStore = db.createObjectStore(PENDING_SALES_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          pendingStore.createIndex('by-queued', 'queuedAt', { unique: false });
        }

        // -- Sync metadata store --
        if (!db.objectStoreNames.contains(SYNC_META_STORE)) {
          db.createObjectStore(SYNC_META_STORE, {
            keyPath: 'key',
          });
        }
      },
    });
  }

  return dbPromise;
}

// ---------------------------------------------------------------------------
// Product catalog operations
// ---------------------------------------------------------------------------

/**
 * Save or update a product in the offline catalog.
 * Evicts oldest entries if the store exceeds MAX_PRODUCTS.
 */
export async function saveProduct(product: Omit<ProductRecord, 'cachedAt'>): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(PRODUCT_STORE, 'readwrite');

  const record: ProductRecord = {
    ...product,
    cachedAt: Date.now(),
  };

  await tx.store.put(record);
  await tx.done;

  // Evict oldest if over limit
  await trimProductStore();
}

/**
 * Look up a product by its barcode (GTIN / code field).
 * Returns the product or undefined if not found.
 */
export async function getProductByBarcode(
  barcode: string,
): Promise<ProductRecord | undefined> {
  const db = await getDb();
  const index = db.transaction(PRODUCT_STORE).store.index('by-code');
  return index.get(barcode);
}

/**
 * Look up a product by its internal code (also stored in `code`).
 * Alias for symmetry — both barcode and internal code use the same field.
 */
export async function getProductByCode(
  code: string,
): Promise<ProductRecord | undefined> {
  return getProductByBarcode(code);
}

/**
 * Search products by name (case-insensitive prefix/contains match).
 * Returns up to `limit` results.
 */
export async function searchProducts(
  query: string,
  limit = 50,
): Promise<ProductRecord[]> {
  const db = await getDb();
  const index = db.transaction(PRODUCT_STORE).store.index('by-name');
  const results: ProductRecord[] = [];
  const lowerQuery = query.toLowerCase();

  let cursor = await index.openCursor();

  while (cursor && results.length < limit) {
    const name = cursor.value.name.toLowerCase();
    if (name.includes(lowerQuery)) {
      results.push(cursor.value);
    }
    cursor = await cursor.continue();
  }

  return results;
}

/**
 * Get a single product by its ID.
 */
export async function getProductById(
  id: string,
): Promise<ProductRecord | undefined> {
  const db = await getDb();
  return db.get(PRODUCT_STORE, id);
}

/**
 * Get total count of cached products.
 */
export async function getProductCount(): Promise<number> {
  const db = await getDb();
  return db.count(PRODUCT_STORE);
}

/**
 * Remove all cached products (useful on full re-sync).
 */
export async function clearProducts(): Promise<void> {
  const db = await getDb();
  await db.clear(PRODUCT_STORE);
}

// ---------------------------------------------------------------------------
// Pending sales operations
// ---------------------------------------------------------------------------

/**
 * Queue a sale that was created while offline.
 * Auto-generates an offlineOperationId (UUID v4) for idempotency.
 * Returns the auto-generated ID of the pending sale record.
 */
export async function savePendingSale(
  tenantId: string,
  payload: Record<string, unknown>,
): Promise<number> {
  const db = await getDb();

  const record: PendingSaleRecord = {
    offlineOperationId: uuidv4(),
    tenantId,
    payload,
    queuedAt: new Date().toISOString(),
    retryCount: 0,
  };

  const id = await db.add(PENDING_SALES_STORE, record);
  return id as number;
}

/**
 * Retrieve all pending (unsynced) sales, oldest first.
 */
export async function getPendingSales(): Promise<PendingSaleRecord[]> {
  const db = await getDb();
  const index = db.transaction(PENDING_SALES_STORE).store.index('by-queued');
  return index.getAll();
}

/**
 * Remove a pending sale once it has been successfully synced.
 */
export async function removePendingSale(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(PENDING_SALES_STORE, id);
}

/**
 * Increment the retry count and store the last error for a pending sale.
 */
export async function markPendingSaleError(
  id: number,
  errorMessage: string,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(PENDING_SALES_STORE, 'readwrite');
  const record = await tx.store.get(id);

  if (record) {
    record.retryCount += 1;
    record.lastError = errorMessage;
    await tx.store.put(record);
  }

  await tx.done;
}

/**
 * Clear ALL local offline data.
 * Removes all pending sales, cached products, and sync metadata.
 * Call after a full resync session completes to comply with NFR-POS-001:
 * "dados offline do PDV devem ser eliminados após sincronização confirmada".
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await getDb();
  await db.clear(PRODUCT_STORE);
  await db.clear(PENDING_SALES_STORE);
  await db.clear(SYNC_META_STORE);
}

/**
 * Get the count of pending sales.
 */
export async function getPendingSalesCount(): Promise<number> {
  const db = await getDb();
  return db.count(PENDING_SALES_STORE);
}

// ---------------------------------------------------------------------------
// Sync metadata operations
// ---------------------------------------------------------------------------

/**
 * Get the last sync timestamp.
 * Returns null if no sync has occurred yet.
 */
export async function getLastSync(): Promise<string | null> {
  const db = await getDb();
  const record = await db.get(SYNC_META_STORE, 'lastSync');
  return record?.value ?? null;
}

/**
 * Set the last sync timestamp.
 */
export async function setLastSync(date: Date | string): Promise<void> {
  const db = await getDb();
  const value = typeof date === 'string' ? date : date.toISOString();

  await db.put(SYNC_META_STORE, {
    key: 'lastSync',
    value,
  });
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * If the product store exceeds MAX_PRODUCTS, delete the oldest entries
 * (by cachedAt) until it is within the limit.
 */
async function trimProductStore(): Promise<void> {
  const db = await getDb();
  const count = await db.count(PRODUCT_STORE);

  if (count <= MAX_PRODUCTS) {
    return;
  }

  const tx = db.transaction(PRODUCT_STORE, 'readwrite');
  const excess = count - MAX_PRODUCTS;

  // Delete oldest entries one by one
  let cursor = await tx.store.openCursor();
  let deleted = 0;

  while (cursor && deleted < excess) {
    cursor.delete();
    cursor = await cursor.continue();
    deleted++;
  }

  await tx.done;
}
